// A character's journey: raids done, where, with whom, and the loot along the
// way. Opened from the character card on the Profile page; works the same for
// sleeping or retired characters. Data: GET /characters/:id/journey (owner only).
import React, { useEffect, useState } from "react";
import { DISPLAY } from "./alignmentUi";
import { tc, useLegible } from "./theme/legible";
import { getWowClassColor } from "./wowClassColor";
import { wowheadItemUrl, refreshWowheadTooltips } from "./wowhead";
import { shortGameName } from "./lib/gameName";

type Journey = {
  character: {
    id: string; name: string; className: string | null; spec: string | null; renderUrl: string | null;
    gameName: string; gameCode: string | null; guildName: string | null; guildLogoUrl: string | null;
    joinedAt: string | null; isSleeping: boolean; sleptAt: string | null;
  };
  stats: { raids: number; bench: number; firstRaid: string | null; lastRaid: string | null; loot: number };
  byInstance: { instance: string; image: string | null; count: number }[];
  companions: { characterId: string; name: string; className: string | null; playerName: string | null; raids: number }[];
  recentRaids: { id: string; name: string; instance: string | null; guildName: string | null; startsAt: string; role: string | null; bench: boolean }[];
  loot: { id: string; awardedAt: string; itemId: string | null; name: string | null; quality: number | null; slot: string | null; boss: string | null; raid: string | null }[];
};

// WoW item quality colours; loot without a stored quality reads as epic.
const QUALITY: Record<number, string> = { 2: "#1eff00", 3: "#0070dd", 4: "#a335ee", 5: "#ff8000" };

function fmtDate(iso: string | null, withYear = true) {
  if (!iso) return "·";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 shadow-[var(--c-shadow-sm)]">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--c-ink-3)]">{label}</div>
      <div className="mt-1 text-2xl text-[var(--c-ink)]" style={DISPLAY}>{value}</div>
      {hint && <div className="text-[11px] text-[var(--c-ink-3)]">{hint}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 shadow-[var(--c-shadow-sm)]">
      <h3 className="mb-3 text-sm text-[var(--c-ink)]" style={DISPLAY}>{title}</h3>
      {children}
    </section>
  );
}

export default function CharacterJourney({ characterId, onClose }: { characterId: string; onClose: () => void }) {
  useLegible();
  const [data, setData] = useState<Journey | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/characters/${characterId}/journey`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "This journey is private." : "Could not load the journey.");
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [characterId]);

  useEffect(() => { if (data) setTimeout(refreshWowheadTooltips, 50); }, [data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const c = data?.character;
  const classColor = tc(getWowClassColor(c?.className || null));
  const maxInstance = Math.max(1, ...(data?.byInstance || []).map((b) => b.count));

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-[var(--c-line)] bg-[var(--c-bg)] shadow-[var(--c-shadow-lg)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={c ? `${c.name}'s journey` : "Character journey"}
      >
        {/* Header: art band (a dark island, text over artwork). */}
        <div className="lfm-dark relative overflow-hidden bg-[#0b0e12] px-6 pb-6 pt-8">
          {c?.renderUrl && (
            <img src={c.renderUrl} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-125 object-cover object-top opacity-25 blur-md" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e12] via-[#0b0e12]/60 to-transparent" />
          <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white/80 hover:bg-black/60 hover:text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="relative flex items-end gap-4">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-white/5" style={{ boxShadow: `inset 0 0 0 2px ${classColor}` }}>
              {c?.renderUrl ? <img src={c.renderUrl} alt="" className="h-full w-full object-cover object-top" /> : (
                <div className="flex h-full w-full items-center justify-center text-3xl font-black" style={{ color: classColor }}>{c?.name?.charAt(0) || "?"}</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-amber-300/90">Journey</div>
              <h2 className="truncate text-3xl text-white sm:text-4xl" style={DISPLAY}>{c?.name || "…"}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-white/70">
                {c && <span style={{ color: classColor }}>{[c.spec, c.className].filter(Boolean).join(" ")}</span>}
                {c && <span>· {shortGameName(c.gameName)}</span>}
                {c?.guildName && <span>· {c.guildName}</span>}
                {c?.isSleeping && (
                  <span className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-indigo-300/40 bg-indigo-400/15 px-2 py-0.5 text-xs font-semibold text-indigo-200">
                    <span className="lfm-zzz is-asleep text-sm"><i>z</i><i>z</i><i>Z</i></span>
                    Asleep since {fmtDate(c.sleptAt)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5 sm:p-6">
          {error && <p className="rounded-xl border border-[var(--c-bad-line)] bg-[var(--c-bad-soft)] p-4 text-sm text-[var(--c-bad-ink)]">{error}</p>}
          {!data && !error && <div className="py-16 text-center text-sm text-[var(--c-ink-3)]">Gathering the story so far…</div>}

          {data && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Raids" value={data.stats.raids} hint={data.stats.bench ? `+${data.stats.bench} on the bench` : undefined} />
                <Stat label="First raid" value={fmtDate(data.stats.firstRaid, false)} hint={data.stats.firstRaid ? new Date(data.stats.firstRaid).getFullYear().toString() : undefined} />
                <Stat label="Last raid" value={fmtDate(data.stats.lastRaid, false)} hint={data.stats.lastRaid ? new Date(data.stats.lastRaid).getFullYear().toString() : undefined} />
                <Stat label="Loot" value={data.stats.loot} hint={data.stats.loot === 1 ? "item" : "items"} />
              </div>

              {data.stats.raids === 0 && data.stats.loot === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--c-line-strong)] p-10 text-center text-[15px] text-[var(--c-ink-3)]">
                  No raids yet. The story starts with the first pull.
                </div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Section title="Raids by instance">
                      {data.byInstance.length === 0 ? <p className="text-sm text-[var(--c-ink-3)]">·</p> : (
                        <ul className="space-y-2">
                          {data.byInstance.map((b) => (
                            <li key={b.instance} className="flex items-center gap-3">
                              <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-[var(--c-sunken)]">
                                {b.image && <img src={b.image} alt="" className="h-full w-full object-cover" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className="truncate text-sm font-semibold text-[var(--c-ink)]">{b.instance}</span>
                                  <span className="text-xs font-bold tabular-nums text-[var(--c-ink-2)]">{b.count}</span>
                                </div>
                                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--c-sunken-2)]">
                                  <div className="h-full rounded-full bg-[var(--c-accent)]" style={{ width: `${(b.count / maxInstance) * 100}%` }} />
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Section>

                    <Section title="Raided most with">
                      {data.companions.length === 0 ? <p className="text-sm text-[var(--c-ink-3)]">·</p> : (
                        <ol className="space-y-1.5">
                          {data.companions.map((p, i) => (
                            <li key={p.characterId} className="flex items-center gap-3 rounded-lg px-2 py-1.5 odd:bg-[var(--c-bg-veil)]">
                              <span className="w-4 text-right text-xs font-bold text-[var(--c-ink-4)]">{i + 1}</span>
                              <span className="min-w-0 flex-1 truncate">
                                <span className="text-sm font-bold" style={{ color: tc(getWowClassColor(p.className)) }}>{p.name}</span>
                                {p.playerName && <span className="text-xs text-[var(--c-ink-3)]"> · {p.playerName}</span>}
                              </span>
                              <span className="text-xs font-semibold tabular-nums text-[var(--c-ink-2)]">{p.raids} raid{p.raids === 1 ? "" : "s"}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </Section>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Section title={`Loot (${data.loot.length})`}>
                      {data.loot.length === 0 ? <p className="text-sm text-[var(--c-ink-3)]">Nothing yet.</p> : (
                        <ul className="max-h-80 space-y-1.5 overflow-y-auto pr-1">
                          {data.loot.map((l) => {
                            const q = tc(QUALITY[l.quality ?? 4] || QUALITY[4]);
                            return (
                              <li key={l.id} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 odd:bg-[var(--c-bg-veil)]">
                                <span className="min-w-0 truncate text-sm font-semibold">
                                  {l.itemId ? (
                                    <a href={wowheadItemUrl(l.itemId, data.character.gameCode)} target="_blank" rel="noreferrer" style={{ color: q }} className="hover:underline">
                                      {l.name || `Item ${l.itemId}`}
                                    </a>
                                  ) : <span style={{ color: q }}>{l.name || "Unknown item"}</span>}
                                  {(l.boss || l.raid) && <span className="text-xs font-normal text-[var(--c-ink-3)]"> · {l.boss || l.raid}</span>}
                                </span>
                                <span className="shrink-0 text-xs text-[var(--c-ink-3)]">{fmtDate(l.awardedAt, false)}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </Section>

                    <Section title="Recent raids">
                      <ol className="relative max-h-80 space-y-3 overflow-y-auto border-l border-[var(--c-line)] pl-4 pr-1">
                        {data.recentRaids.map((r) => (
                          <li key={r.id} className="relative">
                            <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${r.bench ? "bg-[var(--c-ink-4)]" : "bg-[var(--c-accent)]"}`} />
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="truncate text-sm font-semibold text-[var(--c-ink)]">{r.instance || r.name}</span>
                              <span className="shrink-0 text-xs text-[var(--c-ink-3)]">{fmtDate(r.startsAt)}</span>
                            </div>
                            <div className="text-xs text-[var(--c-ink-3)]">
                              {[r.guildName, r.role, r.bench ? "bench" : null].filter(Boolean).join(" · ")}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </Section>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
